import React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, Star, Users, CheckCircle, TrendingUp, Heart } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const EmployeeRecognitionSoftware = () => {
  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6 text-primary" />,
      title: "Increased Engagement",
      description: "Recognition software can boost employee engagement by up to 31%"
    },
    {
      icon: <Users className="h-6 w-6 text-primary" />,
      title: "Improved Retention",
      description: "Companies with recognition programs have 31% lower turnover rates"
    },
    {
      icon: <Heart className="h-6 w-6 text-primary" />,
      title: "Stronger Culture",
      description: "Build a positive workplace culture that attracts and retains top talent"
    }
  ];

  const vendors = [
    {
      name: "Assembly",
      rating: 4.9,
      ratingCount: 3000,
      description: "Comprehensive employee recognition platform with peer-to-peer recognition, rewards, and analytics.",
      features: ["Peer Recognition", "Custom Rewards", "Analytics Dashboard", "Slack/Teams Integration"],
      badge: "Editor's Choice"
    },
    {
      name: "Bonusly",
      rating: 4.7,
      ratingCount: 5200,
      description: "Social recognition platform that makes it easy to recognize teammates and track engagement.",
      features: ["Social Recognition", "Points System", "Integrations", "Mobile App"],
      badge: "Popular Choice"
    },
    {
      name: "Kudos",
      rating: 4.8,
      ratingCount: 2000,
      description: "Employee recognition and engagement platform focused on culture building and values alignment.",
      features: ["Values-Based Recognition", "Culture Analytics", "Peer Nominations", "Milestone Tracking"],
      badge: "Best for Culture"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Employee Recognition Software | Updated Annually</title>
        <meta name="description" content="Explore the best employee recognition software to boost engagement and reward contributions. Updated annually with the latest rankings and vendor comparisons." />
        <meta name="keywords" content="best employee recognition software, employee recognition platforms, recognition software comparison, top recognition tools" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Best Employee Recognition Software",
            "description": "Top-rated employee recognition software platforms",
            "itemListElement": vendors.map((vendor, index) => ({
              "@type": "SoftwareApplication",
              "position": index + 1,
              "name": vendor.name,
              "description": vendor.description,
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": vendor.rating,
                "bestRating": 5,
                "ratingCount": vendor.ratingCount
              }
            }))
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <div className="mb-6">
              <nav className="text-sm text-muted-foreground mb-4">
                <a href="/best" className="hover:text-primary">Best Software</a> / Employee Recognition Software
              </nav>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Best Employee Recognition Software
              </h1>
              <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed mb-8">
                Employee recognition software helps organizations celebrate achievements, boost morale, and create a culture of appreciation. 
                These platforms make it easy to recognize contributions, track engagement, and measure the impact of recognition programs.
              </p>
              
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary font-medium">
                <Calendar className="h-4 w-4" />
                Last Updated: January 2026
              </div>
            </div>
          </section>

          {/* Why Recognition Software Matters */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Why Employee Recognition Software Matters</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-l-4 border-l-primary">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      {benefit.icon}
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>

          {/* Annual Rankings */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Annual Rankings</h2>
            <div className="bg-muted/30 rounded-xl p-8">
              <div className="max-w-md mx-auto">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Current Rankings
                    </CardTitle>
                    <CardDescription>
                      Our comprehensive analysis of the best employee recognition software platforms, updated for 2026.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full" asChild>
                      <a href="/best/employee-recognition-software/top">View Top Rankings</a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Vendor Spotlights */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Top Employee Recognition Software</h2>
            <div className="space-y-6">
              {vendors.map((vendor, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center font-bold text-2xl">
                          {vendor.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-2xl">{vendor.name}</CardTitle>
                            {vendor.badge && (
                              <Badge variant="secondary" className="bg-primary/10 text-primary">
                                {vendor.badge}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {/* Render 5 stars with proper fill based on rating */}
                              {[1, 2, 3, 4, 5].map((starIndex) => (
                                <Star 
                                  key={starIndex}
                                  className={`h-4 w-4 ${
                                    starIndex <= Math.floor(vendor.rating) 
                                      ? "fill-yellow-400 text-yellow-400" 
                                      : starIndex === Math.ceil(vendor.rating) && vendor.rating % 1 >= 0.5
                                        ? "fill-yellow-400/50 text-yellow-400"
                                        : "text-gray-300"
                                  }`} 
                                />
                              ))}
                              <span className="font-semibold ml-1">{vendor.rating}</span>
                            </div>
                            <span className="text-muted-foreground">({vendor.ratingCount.toLocaleString()} reviews)</span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground">
                        View Details
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{vendor.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {vendor.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-1 text-sm bg-muted px-3 py-1 rounded-full">
                          <CheckCircle className="h-3 w-3 text-primary" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="px-6 max-w-7xl mx-auto">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Recognition Program?</h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Compare these top platforms and find the perfect solution for your organization's needs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg">
                    Book a Demo
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <a href="/best">View All Categories</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default EmployeeRecognitionSoftware;